// "Uporabniki" (administrators): add colleagues with a temporary password, change roles, disable,
// reset password / 2FA, sign out all devices, unlock a limited sign-in, delete. Security changes
// ask for the administrator's password and code again (ReauthDialog) when the server wants it.
import { useState } from 'react';
import type { ManagedUser, User, UserRole } from '../../../../shared/types';
import { authChanged } from '../../lib/api';
import { createUser, deleteUser, generateTemporaryPassword, listUserSessions, listUsers, updateUser } from '../../lib/account';
import { formatDateTime } from '../../lib/format';
import { normalizeUsername, USERNAME_HINT, USERNAME_PATTERN } from '../../lib/password';
import { copyText } from '../../export';
import { accountErrorText, FormError } from '../../components/AccountForms';
import { confirmDialog, Modal } from '../../components/Dialog';
import { Icon } from '../../components/Icon';
import { Menu, type MenuItem } from '../../components/Menu';
import { ReauthCancelled, useReauth, type Reauth } from '../../components/ReauthDialog';
import { toast } from '../../components/Toasts';
import { LoadError, Loading, SessionList, useLoaded } from './common';

/** The temporary password, shown once after creating a user or resetting the password. */
function TemporaryPassword({ user, password, created, onClose }: { user: User; password: string; created: boolean; onClose: () => void }) {
  const copy = async () => {
    const ok = await copyText(password);
    toast(ok ? 'Geslo je kopirano v odložišče.' : 'Brskalnik ni dovolil kopiranja v odložišče.', { kind: ok ? 'success' : 'warning' });
  };
  return (
    <Modal
      title={created ? 'Uporabnik je dodan' : 'Novo začasno geslo'}
      actions={
        <button type="button" className="btn btn--primary" onClick={onClose} data-autofocus>
          Končano
        </button>
      }
    >
      <p>
        {created ? 'Začasno geslo za' : 'Novo začasno geslo za'} <strong>{user.displayName}</strong> (uporabniško ime <strong>{user.username}</strong>):
      </p>
      <div className="temp-password">
        <code className="temp-password__value">{password}</code>
        <button type="button" className="btn btn--secondary btn--small" onClick={() => void copy()}>
          <Icon name="copy" size={18} /> Kopiraj
        </button>
      </div>
      <p className="muted small">
        Geslo je prikazano samo zdaj. Sporočite ga osebno ali po telefonu, ne po e-pošti. Ob prvi prijavi ga bo moral(-a) zamenjati s svojim in
        vklopiti dvostopenjsko prijavo.
      </p>
    </Modal>
  );
}

function AddUserDialog({
  guard,
  onClose,
  onCreated,
}: {
  guard: Reauth['guard'];
  onClose: () => void;
  onCreated: (user: ManagedUser, password: string) => void;
}) {
  const [displayName, setDisplayName] = useState('');
  const [username, setUsername] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const name = normalizeUsername(username);
  const problem = !displayName.trim() ? 'Vpišite ime in priimek.' : !USERNAME_PATTERN.test(name) ? `Uporabniško ime ni veljavno. ${USERNAME_HINT}` : null;

  const submit = async () => {
    setTried(true);
    if (problem || busy) return;
    setBusy(true);
    setError(null);
    const password = generateTemporaryPassword();
    try {
      const user = await guard(() => createUser({ displayName, username: name, role, password }));
      onCreated(user, password);
    } catch (err) {
      if (!(err instanceof ReauthCancelled)) setError(accountErrorText(err, 'Uporabnika ni bilo mogoče dodati.'));
      setBusy(false);
    }
  };

  return (
    <Modal title="Dodaj uporabnika" onClose={busy ? undefined : onClose}>
      <form
        id="add-user-form"
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <label htmlFor="new-user-name">Ime in priimek</label>
          <input id="new-user-name" type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} maxLength={100} data-autofocus />
        </div>
        <div className="field">
          <label htmlFor="new-user-username">Uporabniško ime</label>
          <input
            id="new-user-username"
            type="text"
            autoCapitalize="off"
            autoCorrect="off"
            spellCheck={false}
            autoComplete="off"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={40}
            aria-describedby="new-user-username-hint"
          />
          <p id="new-user-username-hint" className="muted small">
            {USERNAME_HINT}
          </p>
        </div>
        <div className="field">
          <label htmlFor="new-user-role">Vloga</label>
          <select id="new-user-role" value={role} onChange={(e) => setRole(e.target.value as UserRole)}>
            <option value="user">Uporabnik</option>
            <option value="admin">Skrbnik (upravlja uporabnike in varnost)</option>
          </select>
        </div>
        <p className="muted small">Začasno geslo bo ustvarjeno samodejno in prikazano enkrat, ko uporabnika dodate.</p>
        {error ? <FormError>{error}</FormError> : tried && problem ? <FormError>{problem}</FormError> : null}
        <div className="form-actions">
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>
            Prekliči
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            <Icon name={busy ? 'spinner' : 'plus'} size={20} /> Dodaj
          </button>
        </div>
      </form>
    </Modal>
  );
}

function RenameDialog({ user, onClose, onSaved }: { user: ManagedUser; onClose: () => void; onSaved: (u: ManagedUser) => void }) {
  const [name, setName] = useState(user.displayName);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      onSaved(await updateUser(user.id, { displayName: name.trim() }));
    } catch (err) {
      setError(accountErrorText(err, 'Imena ni bilo mogoče spremeniti.'));
      setBusy(false);
    }
  };
  return (
    <Modal title="Spremeni ime" onClose={busy ? undefined : onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        <div className="field">
          <label htmlFor="rename-user">Ime in priimek</label>
          <input id="rename-user" type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={100} data-autofocus />
        </div>
        {error && <FormError>{error}</FormError>}
        <div className="form-actions">
          <button type="button" className="btn btn--secondary" onClick={onClose} disabled={busy}>
            Prekliči
          </button>
          <button type="submit" className="btn btn--primary" disabled={busy || !name.trim()}>
            <Icon name={busy ? 'spinner' : 'check'} size={20} /> Shrani
          </button>
        </div>
      </form>
    </Modal>
  );
}

function UserSessionsDialog({ user, onClose }: { user: User; onClose: () => void }) {
  const sessions = useLoaded(() => listUserSessions(user.id), 'Seznama naprav ni bilo mogoče naložiti.');
  return (
    <Modal
      title={`Naprave: ${user.displayName}`}
      onClose={onClose}
      actions={
        <button type="button" className="btn btn--primary" onClick={onClose}>
          Zapri
        </button>
      }
    >
      {sessions.error ? (
        <LoadError message={sessions.error} onRetry={sessions.reload} />
      ) : !sessions.data ? (
        <Loading />
      ) : (
        <SessionList sessions={sessions.data} />
      )}
    </Modal>
  );
}

type Dialog =
  | { kind: 'add' }
  | { kind: 'rename'; user: ManagedUser }
  | { kind: 'sessions'; user: ManagedUser }
  | { kind: 'password'; user: ManagedUser; password: string; created: boolean };

function UserRow({ user, self, onAction }: { user: ManagedUser; self: boolean; onAction: (user: ManagedUser, action: UserAction) => void }) {
  // Your own password, 2FA and sessions are managed under "Moj račun" / "Naprave in seje".
  const items: MenuItem[] = [
    { label: 'Spremeni ime', icon: 'pencil', onSelect: () => onAction(user, 'rename') },
    { label: 'Naprave', icon: 'phone', onSelect: () => onAction(user, 'sessions') },
  ];
  // Also for yourself: a guesser may have limited your sign-in from other addresses.
  if (user.loginLimited) items.push({ label: 'Odkleni prijavo', icon: 'lock', onSelect: () => onAction(user, 'unlock') });
  if (!self) {
    items.push(
      {
        label: user.role === 'admin' ? 'Odvzemi skrbniške pravice' : 'Nastavi za skrbnika',
        icon: 'shield',
        onSelect: () => onAction(user, 'role'),
      },
      { label: 'Ponastavi geslo', icon: 'lock', onSelect: () => onAction(user, 'resetPassword'), separator: true },
    );
    if (user.totpEnabled) items.push({ label: 'Ponastavi dvostopenjsko prijavo', icon: 'refresh', onSelect: () => onAction(user, 'reset2fa') });
    items.push(
      { label: 'Odjavi vse naprave', icon: 'logout', onSelect: () => onAction(user, 'revoke') },
      {
        label: user.disabled ? 'Omogoči račun' : 'Onemogoči račun',
        icon: user.disabled ? 'check' : 'close',
        onSelect: () => onAction(user, 'toggleDisabled'),
        separator: true,
      },
      { label: 'Izbriši uporabnika', icon: 'trash', onSelect: () => onAction(user, 'delete'), danger: true },
    );
  }
  return (
    <li className={`user-row${user.disabled ? ' is-disabled' : ''}`}>
      <span className="user-row__avatar" aria-hidden="true">
        <Icon name="user" size={20} />
      </span>
      <span className="user-row__main">
        <span className="user-row__title">
          {user.displayName}
          {self && <span className="muted small"> (vi)</span>}
        </span>
        <span className="user-row__meta">
          {user.username} · {user.lastLoginAt ? `zadnja prijava ${formatDateTime(user.lastLoginAt)}` : 'še ni bil(-a) prijavljen(-a)'}
        </span>
        <span className="user-row__chips">
          {user.role === 'admin' && <span className="chip chip--neutral">Skrbnik</span>}
          {user.totpEnabled ? <span className="chip chip--done">2FA</span> : <span className="chip chip--pending">Brez 2FA</span>}
          {user.mustChangePassword && <span className="chip chip--due">Začasno geslo</span>}
          {user.loginLimited && <span className="chip chip--due">Prijava začasno omejena</span>}
          {user.disabled && <span className="chip chip--error">Onemogočen</span>}
        </span>
      </span>
      <Menu label={`Dejanja: ${user.displayName}`} items={items} heading={user.displayName} />
    </li>
  );
}

type UserAction = 'rename' | 'role' | 'sessions' | 'resetPassword' | 'reset2fa' | 'revoke' | 'unlock' | 'toggleDisabled' | 'delete';

export function UsersSection({ me }: { me: User }) {
  const users = useLoaded(listUsers, 'Seznama uporabnikov ni bilo mogoče naložiti.');
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const reauth = useReauth(me.totpEnabled);
  const { guard } = reauth;
  const close = () => setDialog(null);

  const replace = (u: ManagedUser) => users.set((users.data ?? []).map((x) => (x.id === u.id ? u : x)));

  const run = async (work: () => Promise<void>, failure: string) => {
    try {
      await work();
    } catch (err) {
      // Cancelled at the password prompt: nothing was changed, nothing to report.
      if (err instanceof ReauthCancelled) return;
      toast(accountErrorText(err, failure), { kind: 'error' });
    }
  };

  const onAction = async (user: ManagedUser, action: UserAction) => {
    const name = user.displayName;
    switch (action) {
      case 'rename':
        setDialog({ kind: 'rename', user });
        return;
      case 'sessions':
        setDialog({ kind: 'sessions', user });
        return;
      case 'role': {
        const promote = user.role !== 'admin';
        const ok = await confirmDialog({
          title: promote ? 'Nastavim za skrbnika?' : 'Odvzamem skrbniške pravice?',
          message: promote
            ? `${name} bo lahko dodajal(-a) in brisal(-a) uporabnike, spreminjal(-a) varnostne nastavitve in bral(-a) varnostni dnevnik.`
            : `${name} bo navaden uporabnik brez dostopa do upravljanja uporabnikov in varnosti.`,
          confirmLabel: promote ? 'Nastavi za skrbnika' : 'Odvzemi pravice',
        });
        if (!ok) return;
        await run(async () => {
          replace(await guard(() => updateUser(user.id, { role: promote ? 'admin' : 'user' })));
          toast(promote ? `${name} je zdaj skrbnik.` : `${name} ni več skrbnik.`, { kind: 'success' });
        }, 'Vloge ni bilo mogoče spremeniti.');
        return;
      }
      case 'resetPassword': {
        const ok = await confirmDialog({
          title: 'Ponastavim geslo?',
          message: `${name} dobi novo začasno geslo, ki ga bo moral(-a) ob prijavi zamenjati. Vse naprave uporabnika bodo odjavljene (tudi aplikacija Android).`,
          confirmLabel: 'Ponastavi geslo',
          danger: true,
        });
        if (!ok) return;
        const password = generateTemporaryPassword();
        await run(async () => {
          replace(await guard(() => updateUser(user.id, { resetPassword: password })));
          setDialog({ kind: 'password', user, password, created: false });
        }, 'Gesla ni bilo mogoče ponastaviti.');
        return;
      }
      case 'reset2fa': {
        const ok = await confirmDialog({
          title: 'Ponastavim dvostopenjsko prijavo?',
          message: `To naredite, ko je ${name} izgubil(-a) telefon. Vse naprave uporabnika bodo odjavljene; pri naslednji prijavi bo dvostopenjsko prijavo nastavil(-a) znova, obnovitvene kode prenehajo veljati.`,
          confirmLabel: 'Ponastavi',
          danger: true,
        });
        if (!ok) return;
        await run(async () => {
          replace(await guard(() => updateUser(user.id, { reset2fa: true })));
          toast('Dvostopenjska prijava je ponastavljena.', { kind: 'success' });
        }, 'Dvostopenjske prijave ni bilo mogoče ponastaviti.');
        return;
      }
      case 'revoke': {
        const ok = await confirmDialog({
          title: 'Odjavim vse naprave?',
          message: `Vse prijave uporabnika ${name} bodo končane – v brskalnikih in v aplikaciji Android (npr. ob izgubljenem telefonu).`,
          confirmLabel: 'Odjavi vse',
          danger: true,
        });
        if (!ok) return;
        await run(async () => {
          replace(await guard(() => updateUser(user.id, { revokeSessions: true })));
          toast('Vse naprave uporabnika so odjavljene.', { kind: 'success' });
        }, 'Naprav ni bilo mogoče odjaviti.');
        return;
      }
      case 'unlock': {
        const ok = await confirmDialog({
          title: 'Odklenem prijavo?',
          message: `Po več napačnih geslih je prijava za ${name} začasno omejena – z ene ali več naprav oziroma omrežij (npr. iz pisarne ali od doma). Odklenite jo, ko ste se z uporabnikom pogovorili in veste, da je to bil(-a) on(-a). Če se omejitev kmalu ponovi, nekdo ugiba geslo: naj ga uporabnik zamenja.`,
          confirmLabel: 'Odkleni',
        });
        if (!ok) return;
        await run(async () => {
          replace(await guard(() => updateUser(user.id, { unlockLogin: true })));
          toast('Prijava je odklenjena.', { kind: 'success' });
        }, 'Prijave ni bilo mogoče odkleniti.');
        return;
      }
      case 'toggleDisabled': {
        const disable = !user.disabled;
        const ok = await confirmDialog({
          title: disable ? 'Onemogočim račun?' : 'Omogočim račun?',
          message: disable
            ? `${name} se ne bo mogel(-la) več prijaviti, vse njegove naprave bodo odjavljene. Podatki ostanejo; račun lahko kadar koli znova omogočite.`
            : `${name} se bo lahko znova prijavil(-a).`,
          confirmLabel: disable ? 'Onemogoči' : 'Omogoči',
          danger: disable,
        });
        if (!ok) return;
        await run(async () => {
          replace(await guard(() => updateUser(user.id, { disabled: disable })));
          toast(disable ? 'Račun je onemogočen.' : 'Račun je omogočen.', { kind: 'success' });
        }, 'Računa ni bilo mogoče spremeniti.');
        return;
      }
      case 'delete': {
        const ok = await confirmDialog({
          title: 'Izbrišem uporabnika?',
          message: (
            <>
              <p>
                Uporabnik <strong>{name}</strong> ({user.username}) bo trajno izbrisan.
              </p>
              <p>
                <strong>Izbrisani bodo tudi vsi njegovi opomniki</strong> (so zasebni). Njegove sestanke in nadzore prevzamete vi, zato ostanejo na
                voljo vsem.
              </p>
              <p className="muted small">Če želite dostop le začasno zapreti, račun raje onemogočite.</p>
            </>
          ),
          confirmLabel: 'Izbriši',
          danger: true,
        });
        if (!ok) return;
        await run(async () => {
          await guard(() => deleteUser(user.id));
          users.set((users.data ?? []).filter((u) => u.id !== user.id));
          toast('Uporabnik je izbrisan.', { kind: 'success' });
        }, 'Uporabnika ni bilo mogoče izbrisati.');
        return;
      }
    }
  };

  const list = users.data ?? [];

  return (
    <section className="card" aria-labelledby="users-title" id="uporabniki">
      <div className="card__head">
        <h2 id="users-title" className="card__title">
          <Icon name="users" size={22} /> Uporabniki
        </h2>
        <button type="button" className="btn btn--primary btn--small" onClick={() => setDialog({ kind: 'add' })}>
          <Icon name="plus" size={18} /> Dodaj uporabnika
        </button>
      </div>
      <p className="muted">Vsak sodelavec ima svoj račun. Opomniki so zasebni, sestanki in nadzori so skupni vsem v podjetju.</p>
      {users.error && !users.data ? (
        <LoadError message={users.error} onRetry={users.reload} />
      ) : !users.data ? (
        <Loading />
      ) : (
        <ul className="user-list">
          {list.map((u) => (
            <UserRow key={u.id} user={u} self={u.id === me.id} onAction={(user, action) => void onAction(user, action)} />
          ))}
        </ul>
      )}
      {dialog?.kind === 'add' && (
        <AddUserDialog
          guard={guard}
          onClose={close}
          onCreated={(user, password) => {
            users.set([...list, user]);
            setDialog({ kind: 'password', user, password, created: true });
          }}
        />
      )}
      {dialog?.kind === 'rename' && (
        <RenameDialog
          user={dialog.user}
          onClose={close}
          onSaved={(u) => {
            replace(u);
            close();
            // Your own name is shown in the top bar.
            if (u.id === me.id) authChanged();
          }}
        />
      )}
      {dialog?.kind === 'sessions' && <UserSessionsDialog user={dialog.user} onClose={close} />}
      {dialog?.kind === 'password' && <TemporaryPassword user={dialog.user} password={dialog.password} created={dialog.created} onClose={close} />}
      {reauth.dialog}
    </section>
  );
}
